import StatusBar from "./StatusBar";
import { PickupDeadlineEditCell } from "./DateEditCells";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

// handles the sorting of the Status header within the donor's list of donated materials
const statusSortComparator = (v1, v2, param1, param2) => {
  const row1 = param1.api.getRow(param1.id);
  const row2 = param2.api.getRow(param2.id);

  // calculate availability percentages
  const percentage1 =
    row1.quantity > 0 ? (row1.remaining_qty / row1.quantity) * 100 : 0;
  const percentage2 =
    row2.quantity > 0 ? (row2.remaining_qty / row2.quantity) * 100 : 0;

  const isPaused1 = v1?.toString().toLowerCase() === "paused";
  const isPaused2 = v2?.toString().toLowerCase() === "paused";

  // determine status categories (matching StatusBar logic)
  const isNew1 = !isPaused1 && v1?.toString().toLowerCase() === "new" && percentage1 === 100;
  const isNew2 = !isPaused2 && v2?.toString().toLowerCase() === "new" && percentage2 === 100;

  // assign sort values based on status category
  const getSortValue = (isPaused, isNew, percentage) => {
    if (isPaused) return 500; // paused sinks to bottom
    if (isNew) return 5000;
    if (percentage === 0) return 1000;
    if (percentage < 20) return 2000 + percentage;
    if (percentage < 40) return 3000 + percentage;
    return 4000 + percentage; // Available (40-100%)
  };

  const sortValue1 = getSortValue(isPaused1, isNew1, percentage1);
  const sortValue2 = getSortValue(isPaused2, isNew2, percentage2);

  // compare sort values
  if (sortValue1 < sortValue2) return -1;
  if (sortValue1 > sortValue2) return 1;
  return 0;
};

// format dates as "Jan 5, 2026" for display
const formatDisplayDate = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// creates the table with specific number formats, restrictions and behaviors for the cells in the column
// column fields match database schema: material, quantity, remaining_qty, latest_pickup, status, date_posted, cost, delivery_instructions
// all columns are now read-only (editable: false) but text is copyable
export const createColumns = (
  t,
  getCellClassName,
  darkMode,
  columnVisibility = {},
  selectedCount = 0,
  totalCount = 0,
  onToggleAll = null,
) => {
  const allChecked = totalCount > 0 && selectedCount === totalCount;
  const isIndeterminate = selectedCount > 0 && selectedCount < totalCount;

  const allColumns = [
    {
      field: "__select",
      headerName: "",
      width: 40,
      minWidth: 40,
      sortable: false,
      disableColumnMenu: true,
      resizable: false,
      renderHeader: () => onToggleAll ? (
        <div
          onClick={(e) => { e.stopPropagation(); onToggleAll(); }}
          title={allChecked ? t("Unselect all") : t("Select all")}
          style={{
            width: 15,
            height: 15,
            borderRadius: 4,
            border: allChecked || isIndeterminate
              ? "2px solid #669d2e"
              : "2px solid rgba(255,255,255,0.25)",
            background: allChecked || isIndeterminate ? "#669d2e" : "transparent",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "border-color 0.15s, background 0.15s",
          }}
        >
          {isIndeterminate && <div style={{ width: 7, height: 2, background: "white", borderRadius: 1 }} />}
          {allChecked && (
            <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
              <path d="M1 3.5L3.2 5.8L8 1" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
      ) : null,
      renderCell: () => null,
    },
    {
      field: "material",
      headerName: t("Material Type"),
      flex: 2,
      editable: false,
      cellClassName: getCellClassName,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const itemName = params.row?.item_name;
        const isDuplicate = !!params.row?.duplicated_from;
        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", lineHeight: 1.3 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span>{params.value}</span>
              {isDuplicate && (
                <span style={{
                  fontSize: "0.6rem",
                  fontWeight: 600,
                  letterSpacing: "0.03em",
                  padding: "1px 5px",
                  borderRadius: 4,
                  backgroundColor: darkMode ? "rgba(245, 158, 11, 0.15)" : "rgba(245, 158, 11, 0.12)",
                  color: darkMode ? "#fbbf24" : "#b45309",
                  lineHeight: 1.4,
                  whiteSpace: "nowrap",
                }}>{t("Duplicated")}</span>
              )}
            </div>
            {itemName && (
              <div style={{ fontSize: "0.75rem", opacity: 0.6, fontStyle: "italic" }}>
                {itemName}
              </div>
            )}
          </div>
        );
      },
    },
    {
      field: "quantity",
      headerName: t("Quantity Total (lbs)"),
      flex: 1.5,
      editable: false, // read-only
      type: "number",
      cellClassName: getCellClassName,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "remaining_qty",
      headerName: t("Quantity Left (lbs)"),
      flex: 1.5,
      editable: false, // read-only
      type: "number",
      cellClassName: getCellClassName,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "cost",
      headerName: t("Cost ($)"),
      flex: 1.2,
      editable: false, // read-only
      type: "number",
      cellClassName: getCellClassName,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value) => {
        return value != null ? `$${Number(value).toFixed(2)}` : "";
      },
    },
    {
      field: "latest_pickup",
      headerName: t("Pickup Deadline"),
      flex: 1.2,
      editable: false, // read-only
      type: "date",
      valueGetter: (value) => value && new Date(value),
      valueFormatter: (value) => formatDisplayDate(value),
      renderCell: (params) => {
        const date = params.value;
        const dateStr = formatDisplayDate(date);
        if (!date || !dateStr) return "";
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const isExpired = date < now;
        if (!isExpired) return dateStr;
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#ea580c" }} title={t("Pickup deadline has passed")}>
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: "0.7rem" }} />
            {dateStr}
          </span>
        );
      },
      cellClassName: getCellClassName,
      headerAlign: "center",
      align: "center",
      renderEditCell: (params) => <PickupDeadlineEditCell {...params} />,
    },
    {
      field: "status",
      headerName: t("Status"),
      flex: 1,
      editable: false, // read-only
      type: "singleSelect",
      valueOptions: ["Available", "New", "Paused"],
      sortComparator: statusSortComparator,
      sortingOrder: ["desc", "asc"], // default to descending (New first)
      renderCell: (params) => (
        <StatusBar
          status={params.value}
          quantityLeft={params.row.remaining_qty}
          quantityTotal={params.row.quantity}
          darkMode={darkMode}
        />
      ),
      cellClassName: getCellClassName,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "date_posted",
      headerName: t("Date Posted"),
      flex: 1.2,
      editable: false, // read-only
      type: "date",
      valueGetter: (value) => value && new Date(value),
      valueFormatter: (value) => formatDisplayDate(value),
      cellClassName: getCellClassName,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "delivery_instructions",
      headerName: t("Delivery Instructions"),
      flex: 2,
      editable: false, // read-only
      cellClassName: getCellClassName,
      headerAlign: "center",
      align: "left",
    },
  ];

  // filter columns based on visibility
  return allColumns.filter(
    (column) => column.field === "__select" || columnVisibility[column.field] !== false
  );
};